import { useGetWeightScaleQuery } from "app/store/patient/api/patientApi";
import { scaleChartTooltipHandler } from "./scaleChartTooltipHandler";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";

const useScaleChart = ({ patientId, color }) => {
  const labelColors = color === "dark" ? "#fff" : "black";
  const gridColor = color === "dark" ? "#a4a4a4" : "#333";

  const options = {
    layout: {
      padding: {
        right: 65,
      },
    },
    responsive: true,
    devicePixelRatio: 4,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        mode: "point",
        position: "nearest",
        external: scaleChartTooltipHandler,
      },
      datalabels: {
        color: labelColors,
        align: "start",
        font: { size: 20 },
        formatter: function (value, context) {
          if (
            context.dataIndex === context.dataset.data.length - 1 &&
            context.dataset.type !== "scatter"
          ) {
            return value.y;
          }
          return "";
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: labelColors,
          font: { size: 14 },
        },
        grid: {
          display: true,
          color: (context) => {
            if (context.tick.value !== 0) {
              return "rgba(0, 0, 0, 0)";
            }
            return gridColor;
          },
        },
      },
      y: {
        grid: {
          color: gridColor,
        },
        beginAtZero: true,
        max: 500,
        ticks: {
          color: labelColors,
          font: { size: 16 },
          stepSize: 60,
          autoSkip: false,
          callback: function (val) {
            if (
              val === 0 ||
              val === 60 ||
              val === 120 ||
              val === 240 ||
              val === 500
            ) {
              return this.getLabelForValue(val);
            }
          },
        },
        title: {
          display: true,
          text: "Pounds (lb)",
          font: { size: 16 },
          color: labelColors,
        },
      },
    },
  };

  const dispatch = useDispatch();
  const [filteredChartData, setFilteredChartData] = useState();
  const [chartData, setChartData] = useState();
  const { data, isLoading } = useGetWeightScaleQuery(patientId, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (data) {
      setFilteredChartData(filterMultipleWeightScaleReadings(data));
    }
  }, [data]);

  useEffect(() => {
    if (filteredChartData) {
      const chartData = {
        labels: filteredChartData?.newLabelsArray,
        datasets: [
          {
            label: "Weight",
            data: filteredChartData?.lineChartArray,
            borderColor: "#0096bd",
            backgroundColor: "#0096bd",
          },
          {
            type: "scatter",
            label: "Scatter",
            borderColor: "#0096bd",
            backgroundColor: "#0096bd",
            data: filteredChartData?.scatteredArray,
          },
        ],
      };

      setChartData(chartData);
    }
  }, [filteredChartData]);

  const filterMultipleWeightScaleReadings = (chartData) => {
    const { data, labels } = chartData;
    const newLabels = new Set(labels);
    const newLabelsArray = Array.from(newLabels);

    const lineChartObject = {};
    const scatteredObject = {};

    labels.forEach((label, i) => {
      lineChartObject[label] = data[i];
    });

    labels.forEach((label, i) => {
      if (lineChartObject[label] !== data[i]) {
        if (Array.isArray(scatteredObject[label])) {
          scatteredObject[label].push(data[i]);
        } else {
          scatteredObject[label] = [data[i]];
        }
      }
    });

    const scatteredArray = [];
    const lineChartArray = Object.values(lineChartObject);
    Object.keys(scatteredObject).forEach((key) => {
      scatteredObject[key].forEach((value) => {
        const obj = {
          x: key,
          y: value,
        };
        scatteredArray.push(obj);
      });
    });

    return { lineChartArray, scatteredArray, newLabelsArray };
  };

  return { options, chartData, isLoading, data, dispatch };
};

export default useScaleChart;
